import React from "react";
import { Hint, Crosshair, ChartLabel } from "react-vis";

export const defaultLabel = "N/A";
export const label = (l) => (l !== null ? l : defaultLabel);

export const colors = [
  "#5757DD",
  "#FFD166",
  "#06D6A0",
  "#EF476F",
  "#88CCF1",
  "#CFFFB3",
  "#B8CDF8",
  "#95F2D9",
  "#A1E8AF",
  "#D4B2D8",
];
export const color = (i, label, offset = 0, gradient = false) => {
  if (label && label == defaultLabel) return "#eaeaea";
  if (i + offset >= colors.length) return "#555555";
  return colors[i + offset];
};

export const defaultChartHeight = "300px";
export const defaultChartHeightRaw = 300;

export const formatNo = (no) => parseFloat(no).toLocaleString("fr");

/**
 * Crée un ranking des valeurs, où la plus grande valeur a le rang `start`. Il peut y avoir des _ex aequo_.
 */
export function* makeRanking(values, start = 0, step = 1) {
  const sorted = [...values].sort((a, b) => b - a);
  let last = sorted[0];
  let currentRank = start;
  for (const value of sorted) {
    if (value !== last) currentRank += step;
    yield { value, rank: currentRank };
    last = value;
  }
}

/**
 * Retourne une exponentielle décroissante passant par `(0, yMin)` et `(xMax, yMax)`.
 */
export function decreasingExponential(yMin, xMax, yMax) {
  const coeff = (Math.log(yMax) - Math.log(yMin)) / xMax;
  return (x) => yMax * Math.exp(-coeff * x);
}

export const sortFunction = (a, b, key) => {
  if (a[key] == defaultLabel) return 1;
  if (b[key] == defaultLabel) return -1;
  if (a[key] == "Autre" || a[key] == "Autres") return 1;
  if (b[key] == "Autre" || b[key] == "Autres") return -1;

  if (a[key] < b[key]) return -1;
  if (a[key] > b[key]) return 1;
  return 0;
};

export const sortFunctionByValue = (a, b, asc = true) => {
  if (a.label == defaultLabel) return 1;
  if (b.label == defaultLabel) return -1;
  if (a.label == "Autre" || a.label == "Autres") return 1;
  if (b.label == "Autre" || b.label == "Autres") return -1;

  return asc ? a.value - b.value : b.value - a.value;
};

/**
 * Crée un range de valeurs suivant une `repartition` dans des groupes de `max` valeurs.
 * Exemple : `dividedRange([2, 0, 3, 0, 4], 6)` --> `[[ 0, 0, 2, 2, 2, 4 ], [ 4, 4, 4 ]]`
 */
export function dividedRange(repartition, max) {
  const count = repartition.reduce((a, b) => a + b, 0);
  const frac = count / max;
  const fullGroups = Math.floor(frac);
  const remainingCells = Math.round((frac - fullGroups) * max);
  const result = [];

  const next = (() => {
    let groupIndex = 0;
    let n = 1;
    return () => {
      while (repartition[groupIndex] === 0) {
        n = 1;
        groupIndex++;
      }
      if (n === repartition[groupIndex]) {
        n = 1;
        return groupIndex++;
      }
      n++;
      return groupIndex;
    };
  })();

  result.push(
    ...Array(fullGroups)
      .fill()
      .map(() => Array(max).fill().map(next)),
  );
  remainingCells && result.push(Array(remainingCells).fill().map(next));
  return result;
}

export const renderXAxisLabel = (label) => (
  <ChartLabel
    className="x-axis-label"
    text={label}
    includeMargin={false}
    xPercent={0.5}
    yPercent={1.19}
    style={{ textAnchor: "middle" }}
  />
);

export const renderYAxisLabel = (label) => (
  <ChartLabel
    className="y-axis-label"
    text={label}
    includeMargin={false}
    xPercent={0}
    yPercent={0.5}
    style={{ transform: "translate(-40 0) rotate(-90)", textAnchor: "middle" }}
  />
);

export const renderDiscrete = (data) => (
  <span>
    {data.label}
    <em>:</em>
    <b>{formatNo(data.value)}</b>
  </span>
);

export const findMinMax = (arr, key, padding = 0, max_only = false) => {
  if (arr.length === 0) return [-Infinity, Infinity];
  let min = arr[0][key],
    max = arr[0][key];

  for (let i = 1, len = arr.length; i < len; i++) {
    let v = arr[i][key];
    min = v < min ? v : min;
    max = v > max ? v : max;
  }

  max = Number(max) + padding;
  if (max_only) return max < 5 ? 5 : max;
  return [min, max < 5 ? 5 : max];
};

export const renderHint = (hint, html) => (
  <Hint value={hint}>
    {html ? (
      html
    ) : (
      <div className="vis-hint">
        <h4>
          {hint.label} : {hint.value}
        </h4>
      </div>
    )}
  </Hint>
);

export const renderCrosshair = (crosshair, html) => (
  <Crosshair values={crosshair}>
    {html ? (
      html
    ) : (
      <div className="vis-crosshair">
        <b>{(crosshair[0] || {}).y}</b>
      </div>
    )}
  </Crosshair>
);

export const renderTick = (t, i) => {
  if (!isNaN(t)) return formatNo(t);
  return t;
};
